import { useNavigate } from 'react-router-dom'
import { ICONS } from '../../../common/constants'
import fetchAPI from '../../../common/fetchAPI'
import useMatchMutate from '../../../common/hooks/useMatchMutate'
import useAuth from '../../../common/hooks/useAuth'
import useModal from '../../../common/hooks/useModal'
import useToast from '../../../common/hooks/useToast'
import ResourceList from '../../../components/admin/ResourceList'
import ConfirmModalContent from '../../../components/common/ConfirmModalContent'
import InlineStack from '../../../components/common/InlineStack'
import BlockStack from '../../../components/common/BlockStack'

const filterFields = {
    sort: {
        title: {
            label: 'Title',
            dbFields: ['title'],
            asc: true,
        },
        createdAt: {
            label: 'Created at',
            dbFields: ['createdAt'],
            asc: false,
        },
    },
}

// The API only allows filtering by orgs the user is a member of (unless admin/assistant)
function getOrgFilterFields(memberUserId) {
    return {
        search: true,
        ...filterFields,
        filters: {
            organizationId: {
                label: 'Organization',
                dbField: 'organizationId',
                options: [
                    {
                        label: 'Any',
                        value: 'undefined',
                    },
                ],
                liveSearchUrl: `/v1/organizations?fields=name,acronym&limit=20&page=1${
                    memberUserId ? `&memberUserId=${memberUserId}` : ''
                }`,
                liveSearchGetResultValue: (result) => result.name,
            },
        },
    }
}

export default function ProjectsList({ orgId, enablePullToRefresh }) {
    const navigate = useNavigate()
    const { setModal, setModalLoading, isModalLoading } = useModal()
    const setToast = useToast()
    const matchMutate = useMatchMutate()
    const auth = useAuth()

    const canFilterAnyOrg = auth.isAdmin || auth.isAssistant

    function handleDelete(item) {
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)

                    const { error } = await fetchAPI(
                        `/v1/tasks/projects/${item.id}`,
                        {},
                        'DELETE',
                    )

                    setModal(null)
                    setModalLoading(false)

                    if (error) {
                        setToast(error, 'alert')
                        return
                    }

                    setToast('Project deleted')
                    matchMutate(/\/v1\/tasks\/projects/)
                }}
            />,
            'Are you sure you want to delete this project?',
        )
    }

    const filters = orgId
        ? filterFields
        : getOrgFilterFields(canFilterAnyOrg ? undefined : auth.user.id)

    return (
        <ResourceList
            enablePullToRefresh={enablePullToRefresh}
            newItemBtnText={orgId ? 'New project' : null}
            renderMainButton={!orgId}
            newItemLabel={'New project'}
            newItemPath={`/projects/new${
                orgId ? `?organizationId=${orgId}` : ''
            }`}
            apiUrl={'/v1/tasks/projects'}
            getItemsFromResponse={(data) => data?.results}
            itemClickPath={'/projects/:item.id'}
            paginated={true}
            initialQuery={{
                organizationId: orgId || undefined,
                populate: 'tasksCount,organization.name',
            }}
            paginationLimit={20}
            filters={filters}
            fields={[
                {
                    column: 1,
                    getValue: (item) => (
                        <BlockStack gap={'tiny'}>
                            {item.title}

                            <InlineStack spaceBetween gap="md">
                                {item.organization?.name ? (
                                    <small className="text-subdued">
                                        {item.organization.name}
                                    </small>
                                ) : null}
                                <InlineStack gap={'sm'}>
                                    {item.phases?.length ? (
                                        <small className="text-subdued">{`${item.phases?.length} phases`}</small>
                                    ) : null}

                                    {item.tasksCount ? (
                                        <small className="text-subdued">{`${item.tasksCount} tasks`}</small>
                                    ) : null}
                                </InlineStack>
                            </InlineStack>
                        </BlockStack>
                    ),
                    getClassName: () => 'title',
                },
            ]}
            actions={[
                {
                    title: 'Edit',
                    onClick: (item) => navigate(`/projects/${item.id}/edit`),
                    icon: ICONS.EDIT_GRAY,
                },
                {
                    title: 'Permissions (Beta)',
                    link: '/settings/permissions/project/:item.id',
                    icon: ICONS.LOCK_GRAY,
                },
                {
                    title: 'Delete',
                    onClick: handleDelete,
                    icon: ICONS.TRASH_RED,
                    type: 'alert',
                },
            ]}
        />
    )
}
